import React, { useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import { getText } from "../configure/utils";
import { useEnv } from "./utils";
import collapse from "../../assets/chat/collapse-white-32.png";

const ReRegisMFAChatBox = () => {
  const { language } = useContext(LanguageContext);
  const { isValid, values, error } = useEnv(["DIALOGFLOW_CX_PROJECT_ID", "DIALOGFLOW_CX_Re_Resitor_MFA"]);

  const dfMessengerRef = useRef(null);
  const languageRef = useRef(language || "th");
  const isInitializedRef = useRef(false);

  const NEW_SESSION = useMemo(
    () => [
      {
        type: "button",
        icon: { type: "chevron_right", color: "#FF9800" },
        mode: "blocking",
        text: getText("df_cx.timeout.button"),
        event: { event: "greeting" },
      },
    ],
    [language]
  );

  const initializeChat = useCallback((messenger) => {
    if (!messenger || isInitializedRef.current) return;
    messenger.startNewSession({ retainHistory: false });
    setTimeout(() => {
      messenger.sendRequest("event", "greeting");
      isInitializedRef.current = true;
    }, 500);
  }, []);

  const handleSentRequest = useCallback((event) => {
    const queryInput = event.detail?.data?.requestBody?.queryInput;
    if (queryInput) {
      queryInput.languageCode = languageRef.current;
    }
  }, []);

  const handleSessionExpired = useCallback(() => {
    if (!dfMessengerRef.current) return;
    dfMessengerRef.current.renderCustomText(getText("df_cx.timeout.message"), true);
    dfMessengerRef.current.renderCustomCard(NEW_SESSION);
  }, [NEW_SESSION]);

  const handleClickButton = useCallback((event) => {
    const messenger = dfMessengerRef.current;
    if (!messenger || !event?.detail) return;

    const { event: evt, text } = event.detail;
    // If already have event in button
    if (evt) {
      messenger.renderCustomText(text, false);
    } else if (text) {
      messenger.renderCustomText(text, false);
      messenger.sendRequest("query", text);
    }
  }, []);

  const handleChatSession = useCallback(
    (event) => {
      if (!event.detail.message || !dfMessengerRef.current) return;

      isInitializedRef.current = false;
      initializeChat(dfMessengerRef.current);
    },
    [initializeChat]
  );

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    const messenger = document.querySelector(".mfaResetChat df-messenger");
    if (!messenger) return;

    const bubble = messenger.querySelector("df-messenger-chat-bubble");
    if (!bubble) return;
    bubble.openChat();

    dfMessengerRef.current = messenger;
    const timeoutId = initializeChat(messenger);

    return () => {
      clearTimeout(timeoutId);
      isInitializedRef.current = false;
    };
  }, [initializeChat]);

  useEffect(() => {
    const events = {
      "df-request-sent": handleSentRequest,
      "df-session-expired": handleSessionExpired,
      "df-button-clicked": handleClickButton,
      "df-mfa-clear-chat-session": handleChatSession,
    };

    for (const [key, handler] of Object.entries(events)) {
      window.addEventListener(key, handler);
    }

    return () => {
      for (const [key, handler] of Object.entries(events)) {
        window.removeEventListener(key, handler);
      }
    };
  }, [handleSentRequest, handleSessionExpired, handleClickButton, handleChatSession]);

  if (!isValid) {
    console.error("MFA Chat config invalid:", error);
    return null;
  }

  return (
    <div className="mfaResetChat">
      <df-messenger
        project-id={values.DIALOGFLOW_CX_PROJECT_ID}
        agent-id={values.DIALOGFLOW_CX_Re_Resitor_MFA}
        allow-feedback="all"
        location="asia-southeast1"
        style={{
          "--df-messenger-titlebar-background": "#0078D4",
          "--df-messenger-chat-bubble-background": "#0078D4",
          "--df-messenger-chips-background": "#0078D4",
          "--df-messenger-chips-box-shadow": "#4f6ff14d",
          "--df-messenger-files-background": "#0078D4",
          "--df-messenger-message-user-background": "#c4d8ffb3",
          "--df-messenger-files-font-color": "#ffffff",
          "--df-messenger-files-icon-font-color": "#ffffff",
          "--df-messenger-chat-scroll-button-background": "#0078D4",
          "--df-messenger-files-background-hover": "#5387ebb3",
          // "--df-messenger-chips-box-shadow": "#c4d8ffb3",
          "--df-messenger-chips-background-hover": "#5387ebb3",
          "--df-messenger-send-icon-color-active": "#0078D4",
          "--df-messenger-main-color": "#0078D4",
        }}
      >
        <df-messenger-chat-bubble
          chat-title="SANDEE Chatbot"
          chat-subtitle={getText("re_mfa.buttonText")}
          allow-fullscreen="always"
          placeholder-text={getText("placeholdertext")}
          chat-collapse-icon={collapse}
        />
      </df-messenger>
    </div>
  );
};

export default ReRegisMFAChatBox;
